"use client";

import { useCallback, useEffect, useMemo, useRef } from "react";
import { useAccount, useConfig, useDisconnect, useSwitchChain } from "wagmi";
import { connect, getAccount } from "wagmi/actions";
import { useConnectModal } from "@rainbow-me/rainbowkit";

import { mudwaterEvmChainId } from "@/lib/mudwater";
import { createEvmProvider } from "@/lib/wallet/evm-adapter";
import { chainSwitchError } from "@/lib/wallet/evm-errors";
import { isWalletConnectConfigured } from "@/lib/wallet/config";
import { useEvmPickerChange } from "@/lib/wallet/use-evm-picker-change";
import { rumEvent } from "@/lib/faro";

import type { EvmProvider } from "@/lib/mudwater";

export interface EvmWallet {
  evmAddress: string | null;
  evmWalletName?: string;
  ethereum: EvmProvider | null;
  connectEvm: () => Promise<void>;
  addEvmNetwork: () => Promise<void>;
  disconnectEvm: () => void;
  changeEvm?: (signal?: AbortSignal) => Promise<void>;
}

const NO_PICKER = "The EVM wallet picker is unavailable. Reload and try again.";

export function useEvmWallet(): EvmWallet {
  const config = useConfig();
  const { address, status, chainId, connector } = useAccount();
  const { switchChainAsync } = useSwitchChain();
  const { disconnectAsync } = useDisconnect();
  const { openConnectModal } = useConnectModal();

  // Publishing the address only while the wallet is on the right chain keeps
  // the old invariant the devnet faucet's eth_chainId check relies on.
  const onExpectedChain =
    status === "connected" && chainId === mudwaterEvmChainId;
  const evmAddress = onExpectedChain ? (address ?? null) : null;

  const ethereum = useMemo(
    () =>
      evmAddress && connector
        ? createEvmProvider(() => {
            const account = getAccount(config);
            return account.connector
              ? account.connector.getProvider()
              : Promise.resolve(undefined);
          })
        : null,
    [config, evmAddress, connector]
  );

  // Which EVM wallet a claim went through. Without it, a wallet that signs and
  // never broadcasts (TOTEM_QUOTE_EXPIRED_UNSENT) cannot be named.
  const connectorName = connector?.name;
  useEffect(() => {
    if (evmAddress && connectorName)
      rumEvent("wallet_connected", { chain: "evm", connector: connectorName });
  }, [evmAddress, connectorName]);

  // wagmi's switchChain already falls back to wallet_addEthereumChain on 4902,
  // so add and switch are the same call; the messages stay as they were.
  const switchToMudwater = useCallback(async () => {
    try {
      await switchChainAsync({ chainId: mudwaterEvmChainId });
    } catch (error) {
      throw chainSwitchError(error);
    }
  }, [switchChainAsync]);

  /**
   * A wallet can finish connecting on another chain, and `evmAddress` stays
   * null until it does not — so without this the connection completes and
   * every screen still reports no wallet. Asked once per account: a refusal
   * leaves RainbowKit's own wrong-network control as the way back.
   *
   * Only ever after the holder asked to connect. `WagmiProvider` defaults to
   * `reconnectOnMount`, so an already-authorised wallet reconnects silently on
   * page load — and without this guard a holder who had merely opened the site
   * before was met by a network-permission dialog the moment it loaded, having
   * clicked nothing. Greeting someone with a wallet prompt is how people learn
   * to click through them. A silent reconnect on the wrong chain now simply
   * reports no wallet, and RainbowKit's wrong-network control is the way back.
   */
  const requested = useRef(false);
  const asked = useRef<string | null>(null);
  useEffect(() => {
    if (!requested.current) return;
    if (status !== "connected" || !address) return;
    if (chainId === mudwaterEvmChainId) {
      asked.current = null;
      return;
    }
    if (asked.current === address) return;
    asked.current = address;
    void switchToMudwater().catch(() => undefined);
  }, [address, chainId, status, switchToMudwater]);

  const connectEvm = useCallback(async () => {
    // Set before anything awaits: the connection can complete on the wrong
    // chain while this is still running, and the effect above has to know the
    // holder asked for it by the time it sees that.
    requested.current = true;
    if (status !== "connected") {
      if (!isWalletConnectConfigured) {
        const connector = config.connectors.find(
          (entry) => entry.id === "injected"
        );
        if (!connector || !(await connector.getProvider()))
          throw new Error(
            "Install or unlock a browser EVM wallet, then try again."
          );
        await connect(config, { connector, chainId: mudwaterEvmChainId });
        return;
      }
      if (!openConnectModal) throw new Error(NO_PICKER);
      openConnectModal();
      return;
    }
    await switchToMudwater();
  }, [config, openConnectModal, status, switchToMudwater]);

  const resetRequest = useCallback(() => {
    requested.current = false;
    asked.current = null;
  }, []);
  const openPicker = useCallback(() => {
    if (!openConnectModal) throw new Error(NO_PICKER);
    requested.current = true;
    openConnectModal();
  }, [openConnectModal]);
  const disconnectForChange = useCallback(async () => {
    if (!isWalletConnectConfigured) throw new Error(NO_PICKER);
    await disconnectAsync();
  }, [disconnectAsync]);
  const { change: changeEvm, cancel: cancelChange } = useEvmPickerChange({
    disconnected: status === "disconnected",
    open: openConnectModal ? openPicker : undefined,
    disconnect: disconnectForChange,
    reset: resetRequest,
  });
  const disconnectEvm = useCallback(() => {
    // Disconnecting withdraws the request too, so a later reconnect does not
    // inherit permission to prompt from a connection the holder ended.
    cancelChange();
    resetRequest();
    void disconnectAsync().catch(() => undefined);
  }, [disconnectAsync, cancelChange, resetRequest]);

  return useMemo(
    () => ({
      evmAddress,
      ...(status === "connected" && connectorName
        ? { evmWalletName: connectorName }
        : {}),
      ethereum,
      connectEvm,
      addEvmNetwork: switchToMudwater,
      disconnectEvm,
      changeEvm,
    }),
    [
      connectEvm,
      disconnectEvm,
      changeEvm,
      ethereum,
      evmAddress,
      switchToMudwater,
      status,
      connectorName,
    ]
  );
}
